<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>
          <v-alert v-if="success" type="success" class="mb-4" closable @click:close="success = ''">
            {{ success }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="obra-form-panel">
            <div class="obra-form-header">
              <v-icon size="22" color="#ffffff">mdi-movie-open-outline</v-icon>
              <span>{{ headerTitle }}</span>
            </div>

            <div class="obra-form-body">
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="productora.name"
                    label="Nombre"
                    variant="outlined"
                    :error-messages="nameError"
                    @update:model-value="nameError = ''"
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-switch
                    v-model="productora.active"
                    label="Activa"
                    color="primary"
                    hide-details
                    density="compact"
                  />
                </v-col>
              </v-row>

              <div class="obra-form-actions">
                <button type="button" class="obra-cancel-btn" @click="cancel">
                  CANCELAR Y VOLVER
                </button>
                <button type="button" class="obra-save-btn" :disabled="saving" @click="save">
                  {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                </button>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchProductora,
  createProductora,
  updateProductora,
} from "@/services/teatroApi";

export default {
  name: "ProductoraForm",
  props: {
    // When set (editing), load that productora.
    productoraId: {
      type: [String, Number],
      default: null,
    },
    // When true, render an empty form to create a new productora.
    create: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productora: { name: "", active: true },
      loading: true,
      saving: false,
      error: "",
      success: "",
      nameError: "",
    };
  },
  computed: {
    headerTitle() {
      return this.create ? "NUEVA PRODUCTORA" : "EDITAR PRODUCTORA";
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        if (this.create) {
          this.productora = { name: "", active: true };
        } else if (this.productoraId) {
          this.productora =
            (await fetchProductora(this.productoraId)) || { name: "", active: true };
        }
      } catch (e) {
        this.error = "No se pudo cargar la productora.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.nameError = "";
      if (!String(this.productora.name || "").trim()) {
        this.nameError = "El nombre es obligatorio.";
        return;
      }
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = {
          name: this.productora.name,
          active: !!this.productora.active,
        };
        if (this.create) {
          await createProductora(payload);
        } else {
          await updateProductora(this.productora.id, payload);
        }
        window.location.href = "/productoras/";
      } catch (e) {
        const data = e.response?.data;
        this.error =
          data?.detail || data?.name?.[0] || "No se pudo guardar la productora.";
        console.error(e);
        this.saving = false;
      }
    },
    cancel() {
      window.location.href = "/productoras/";
    },
  },
};
</script>

<style scoped>
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.obra-form-body {
  padding: 30px 34px 28px;
}

.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 18px;
}

.obra-cancel-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.obra-cancel-btn:hover {
  opacity: 0.72;
}

.obra-save-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obra-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.obra-save-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

/* Force the Vuetify fields to the white look of the mockup. */
.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}

.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input) {
  color: #1a1a1a !important;
}

.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}

/* Al flotar, el label queda a caballo entre el campo blanco y el panel oscuro;
   fondo oscuro tipo etiqueta con texto blanco para que siempre contraste. */
.obra-form-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Texto de ayuda (hint) legible sobre el panel oscuro. */
.obra-form-panel :deep(.v-messages),
.obra-form-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}

/* The switch label sits on the dark panel, keep it light. */
.obra-form-panel :deep(.v-switch .v-label) {
  color: var(--color-theater-text) !important;
  opacity: 1;
}
</style>
